import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material"
import { useCallback, useEffect, useId } from "react"
import { useGetOrganizationGroupsQuery } from "../services/groupsApi"
import {
  selectOrganizationGroups,
  setOrganizationGroups,
} from "../store/groupFilterSlice"
import { useAppDispatch, useAppSelector } from "../store/hooks"

type GroupFilterSelectProps = {
  organizationId: string
  value: string[]
  onChange: (value: string[]) => void
}

export function GroupFilterSelect({
  organizationId,
  value,
  onChange,
}: GroupFilterSelectProps) {
  const labelId = useId()
  const {
    data: groups = [],
    isLoading,
    isError,
  } = useGetOrganizationGroupsQuery(organizationId)

  useEffect(() => {
    if (
      !isLoading &&
      !isError &&
      value.some(
        (selectedGroup) => !groups.some((group) => group.id === selectedGroup)
      )
    ) {
      onChange(
        value.filter((selectedGroup) =>
          groups.some((group) => group.id === selectedGroup)
        )
      )
    }
  }, [groups, isError, isLoading, onChange, value])

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const nextValue = event.target.value
    const selectedGroups =
      typeof nextValue === "string" ? nextValue.split(",") : nextValue

    onChange(selectedGroups.includes("") ? [] : selectedGroups)
  }

  return (
    <FormControl size="small" sx={{ minWidth: 240 }} error={isError}>
      <InputLabel id={labelId} shrink>
        Groups
      </InputLabel>
      <Select
        labelId={labelId}
        label="Groups"
        multiple
        value={value}
        onChange={handleChange}
        disabled={isLoading || isError}
        displayEmpty
        renderValue={(selectedGroups) => {
          if (selectedGroups.length === 0) {
            return "All groups"
          }

          return selectedGroups
            .map(
              (groupId) =>
                groups.find((group) => group.id === groupId)?.label || groupId
            )
            .join(", ")
        }}
      >
        <MenuItem value="">
          <Checkbox checked={value.length === 0} />
          <ListItemText primary="All groups" />
        </MenuItem>
        {groups.map((group) => (
          <MenuItem key={group.id} value={group.id}>
            <Checkbox checked={value.includes(group.id)} />
            <ListItemText primary={group.label} />
          </MenuItem>
        ))}
      </Select>
      {isError ? <FormHelperText>Unable to load groups.</FormHelperText> : null}
    </FormControl>
  )
}

export function useGroupFilter(organizationId: string) {
  const dispatch = useAppDispatch()
  const selectedGroups = useAppSelector((state) =>
    selectOrganizationGroups(state, organizationId)
  )

  const setSelectedGroups = useCallback(
    (groupIds: string[]) => {
      dispatch(setOrganizationGroups({ organizationId, groupIds }))
    },
    [dispatch, organizationId]
  )

  return {
    selectedGroups,
    setSelectedGroups,
    groupParams: selectedGroups.length > 0 ? { group: selectedGroups } : {},
  }
}
